import { createTheme } from '@material-ui/core/styles';

// A custom theme for this app
export default ({ primaryColor, textColor, secondaryColor, fontFamily }) => {
    /**
     * palette: {
            primary: {
                main: clientData.primaryColor
            },
            text: {
                primary: clientData.textColor
            },
            secondary: {
                main: clientData.secondaryColor
            }
        },
     typography: {
            fontFamily: clientData.fontFamily
        }
     */
    return createTheme({
        typography: {
            fontFamily
        },
        palette: {
            primary: {
                main: primaryColor
            },
            text: {
                textColor
            },
            secondary: {
                main: secondaryColor
            }
        },
        // Default props for some of the components
        props: {
            MuiPaper: {
                square: false
            },
            MuiTable: {
                size: 'small'
            },
            MuiTabs: {
                indicatorColor: 'primary'
            }
        },
        // Global style overrides
        overrides: {
            MuiDialog: {},
            MuiCssBaseline: {
                '@global': {
                    // remove number input up/down in Chrome, Safari, Edge, Opera
                    'input::-webkit-outer-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0
                    },
                    'input::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0
                    },
                    // remove number input up/down in Firefox
                    'input[type=number]': {
                        '-moz-appearance': 'textfield'
                    }
                }
            },
            // smaller icons in tables
            MuiTable: {
                root: {
                    '& .MuiIconButton-root': {
                        padding: 10
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.2rem'
                    }
                }
            },
            // Dialog titles to center
            MuiDialogTitle: {
                root: {
                    textAlign: 'center'
                }
            },
            MuiIconButton: {
                root: {
                    fontSize: '1.2rem'
                }
            },
            MuiPaper: {
                root: {
                    '&.padded': {
                        padding: 16
                    },
                    '&.smallpadded': {
                        padding: 6,
                        fontSize: 12
                    },
                    '&.colorcard': {
                        borderRadius: 0,
                        borderLeft: `6px solid ${primaryColor}`
                    },
                    '&.colorcard .fullheadline': {
                        background: `${primaryColor}`,
                        color: '#fff',
                        padding: '12px 16px'
                    },
                    '&.cartitem': {
                        display: 'flex',
                        marginBottom: 32
                    },
                    '&.cartitem .MuiCardContent-root:last-child': {
                        flex: '1 0 auto',
                        paddingBottom: 16
                    }
                }
            },
            MuiFormLabel: {
                root: {
                    fontSize: 14
                }
            },
            MuiInputBase: {
                root: {
                    fontSize: 14
                },
                input: {
                    fontSize: 14
                }
            },
            MuiChip: {
                root: {
                    fontSize: 14,
                    height: 23
                }
            },
            MuiTypography: {
                body1: {
                    fontSize: 14
                },
                // Used for form sections
                subtitle1: {
                    margin: '0px 0px 0.35em',
                    fontWeight: 400,
                    fontSize: '1.2rem',
                    lineHeight: 1.334,
                    letterSpacing: '0em'
                },
                h2: {
                    fontWeight: '500',
                    fontSize: '1.5rem'
                }
            },
            MuiGrid: {
                root: {
                    '&.richEditorRoot': {
                        background: '#fff',
                        border: '1px solid #ddd',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        padding: '15px'
                    },
                    '&.richEditorStyleButton': {
                        color: '#999',
                        cursor: 'pointer',
                        marginRight: '16px',
                        padding: '2px 0',
                        display: 'inline-block'
                    },
                    '&.richEditorActiveButton': {
                        color: '#5890ff',
                        cursor: 'pointer',
                        marginRight: '16px',
                        padding: '2px 0',
                        display: 'inline-block'
                    },
                    '&.pageWithSidebar': {
                        display: 'flex'
                    },
                    '&.pageContent': {
                        paddingLeft: 32,
                        flex: '1 1 0'
                    },
                    '&.cartinfo': {
                        fontSize: '.8rem',
                        textTransform: 'uppercase',
                        flexWrap: 'nowrap',
                        alignItems: 'flex-start',
                        whiteSpace: 'pre-line',
                        lineBreak: 'loose'
                    },
                    '&.iconspace': {
                        height: 35,
                        width: 32
                    },
                    '&.searchfilter-wrapper': {
                        padding: '14px',
                        lineHeight: '40px'
                    },
                    // to give a nice one line feel for the search filters
                    '&.searchfilter-wrapper .searchfilter .MuiButtonGroup-groupedOutlinedHorizontal': {
                        borderRadius: '0 0 0 0',
                        borderLeft: 0,
                        borderRight: 0,
                        borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&.searchfilter-wrapper .searchfilter:first-child .MuiButtonGroup-groupedOutlinedHorizontal:first-child': {
                        borderRadius: '4px 0 0 4px',
                        borderLeft: `1px solid rgba(0, 0, 0, 0.23)`
                    },
                    '&.searchfilter-wrapper .searchfilter .MuiButtonGroup-groupedOutlinedHorizontal:last-child': {
                        borderRight: `1px dashed rgba(0, 0, 0, 0.23)`
                    },
                    '&.searchfilter-wrapper .searchfilter:last-child .MuiButtonGroup-groupedOutlinedHorizontal:last-child': {
                        borderRadius: '0 4px 4px 0',
                        borderRight: `1px solid rgba(0, 0, 0, 0.23)`
                    },
                    '&.searchfilter-wrapper .MuiSelect-select': {
                        fontSize: '0.8125rem'
                    },
                    '&.searchfilter-wrapper .MuiInputBase-input': {
                        fontSize: '0.8125rem'
                    },
                    '&.searchfilter-wrapper .MuiSelect-select:focus': {
                        backgroundColor: 'inherit'
                    },
                    '&.statistics-datepicker .MuiPickersDateRangePickerInput-root .datepicker .date-text': {
                        width: 'unset !important'
                    }
                }
            },
            MuiTabs: {
                scrollable: {
                    borderRight: '1px solid rgba(0, 0, 0, 0.23)'
                }
            },
            MuiCardMedia: {
                root: {
                    '&.cartitem': {
                        width: '25%',
                        margin: 16
                    }
                }
            },
            MuiButton: {
                iconSizeMedium: {
                    '& > *:first-child': {
                        fontSize: '1.15em !important'
                    }
                }
            },
            MuiButtonGroup: {
                root: {
                    '&.searchfilter': {
                        height: 30,
                        background: 'transparent'
                    },
                    '&.searchfilter label + .MuiInput-formControl': {
                        marginTop: 0
                    },
                    '&.searchfilter .MuiInputLabel-formControl': {
                        display: 'none'
                    },
                    '&.searchfilter .MuiAutocomplete-endAdornment': {
                        opacity: '.3'
                    },
                    '&.searchfilter .MuiButton-root': {
                        textTransform: 'none',
                        fontWeight: 'normal'
                    },
                    '&.searchfilter .MuiInput-underline:before': {
                        display: 'none'
                    },
                    '&.searchfilter .MuiInput-underline:after': {
                        display: 'none'
                    },
                    '&.searchfilter .MuiButton-outlinedPrimary': {
                        backgroundColor: primaryColor,
                        color: 'inherit'
                    }
                }
            },
            MuiMenuItem: {
                root: {
                    fontSize: '0.875rem'
                }
            }
        }
    });
};
